/**
 * Единый резолв guid покупателя (WEB-1139, ревизия).
 *
 * Один и тот же guid обязан уйти И в связке провайдера (`POST
 * /public/provider-link`), И в чекауте (`visitorId` сессии Stripe): бэкенд ищет
 * связку по guid гранта. Разъедутся — деньги взяты, доступ в Adapty/RevenueCat
 * молча не выдан, никто не уведомлён. Поэтому логика живёт в одном месте, а не
 * дублируется в утилите связки и в визарде пейвола.
 *
 * Правило (НЕ менять без money-review; ревизия WEB-1416/Ф1 — money-review
 * пачки 1 от 22.08; ревизия «якорь SDK» — блокер выката 23.08, см. ниже):
 *  1. `origin=app` + явный `?guid=X` в адресе → guid = X (и он персистится в
 *     sessionStorage) — ДАЖЕ если рядом лежит `?pass=`. Это дописал мобильный
 *     SDK, и X — client-held якорь, по которому SDK потом СПРАШИВАЕТ право
 *     (`GET /public/entitlement?guid=X`). Приоритет отдаётся якорю поллинга:
 *     иначе рвётся связка «оплата → поллинг» (обоснование — ниже);
 *  2. `?pass=` БЕЗ явного `?guid=` → источник правды — обмен пропуска
 *     (resolveAppGuidPass → sessionStorage), идём в п.3 (веб-ссылка из
 *     письма/переписки: дописывать guid там некому);
 *  3. `?guid=` БЕЗ `origin=app` личностью НЕ считается (защита WEB-1416:
 *     сырой guid из адреса не должен затирать verified-результат обмена) —
 *     идём в п.4;
 *  4. иначе — сохранённый app-guid, если он есть (межстраничные переходы и
 *     редиректы чекаута теряют query, но вкладка та же);
 *  5. иначе — обычный веб-visitor-id.
 *
 * БЛОКЕР ВЫКАТА 23.08 (почему п.1 главнее п.2 — money-инвариант).
 * Мобильные SDK (`web2app-ios-sdk` WebPaywallLauncher.swift:29-60,
 * `web2app-android-sdk` WebPaywallLauncher.appOriginUrl) НЕ открывают
 * серверный paywallUrl как есть: они берут ссылку, полученную с бэкенда (в
 * ней уже стоит `?pass=`), и ДОПИСЫВАЮТ к ней свои `origin=app`, `email`,
 * `guid`, где guid — СОБСТВЕННЫЙ идентификатор устройства из Keychain
 * (iOS) / SharedPreferences (Android). После оплаты SDK ПОЛЛИТ
 * `GET /public/entitlement?guid=<этот самый client-held guid>` (Android
 * Web2AppSdk.kt:224, iOS WebPaywallLauncher.swift:12-17) — до появления
 * права. Значит право обязано лечь ИМЕННО на него.
 *
 * Правило ревизии Ф1 («есть pass → сырой guid игнорируется целиком») в этом
 * (штатном!) сценарии уводило оплату на серверный guid из пропуска: покупатель
 * платил, право ложилось на другой идентификатор, а поллинг SDK не видел его
 * НИКОГДА — «заплатил, а приложение доступа не даёт». Поэтому при
 * `origin=app` явный guid из адреса ГЛАВНЕЕ пропуска. Защита WEB-1416 при
 * этом сохранена там, где она и была нужна: без `origin=app` (веб-ссылка с
 * легаси-хвостом) сырой guid по-прежнему личностью не признаётся, а при
 * `pass` без `guid` путь обмена не изменился вовсе.
 *
 * WEB-1416 (аудит 22.08, п.2): до этой правки `?guid=X` в адресе paywallUrl
 * был ГОЛЫМ identity-guid — вечным переиспользуемым ключом. Утёкший адрес
 * (логи интегратора / история браузера мобильного WebView / пересланное
 * сообщение) давал постороннему И оплатить под чужой личностью, И привязать
 * СВОЙ Adapty/RevenueCat к ЧУЖОМУ guid — украсть чужой оплаченный доступ.
 * S2sUsersService.buildPaywallUrl (backend/src/s2s/services/s2s-users.service.ts)
 * теперь минтит короткоживущий подписанный пропуск (ManageLinkTokenService,
 * ТОТ ЖЕ HMAC-механизм, что manage_link, но TTL=24ч) и кладёт его под НОВЫМ
 * параметром `?pass=` — см. resolveAppGuidPass() ниже, обменивает его на
 * verified guid через backend (PublicPortalController.resolvePaywallGuidPass).
 *
 * Обратная совместимость: `resolveAppGuidFromParams`/`resolveAppGuid` (СИНХ-
 * РОННЫЕ, как и раньше) продолжают принимать СТАРЫЙ голый `?guid=` НАПРЯМУЮ,
 * без изменения поведения — уже выданные до этой правки ссылки не ломаются.
 * Единственное добавление на этом пути — best-effort наблюдаемость (см.
 * reportLegacyPaywallGuidUsage): backend должен увидеть ОБЪЁМ легаси-формата
 * перед его отключением (дата отключения — за владельцем).
 *
 * Где подключён обмен пропуска (WEB-1416-wiring, уточнено WEB-3417):
 * resolveAppGuidPass() зовёт ОДНО место — страница пейволла
 * (pages/PublicPaywall.tsx, эффект загрузки): на маунте, ДО первой
 * надобности в личности, с таймаутом PASS_EXCHANGE_TIMEOUT_MS; по ссылке
 * предпросмотра обмен не ходит (WEB-2115). Успешный обмен кладёт guid в
 * sessionStorage под APP_GUID_KEY, и синхронные потребители
 * (PaywallScreenWizard.tsx — чекаут, providerProfileLink.ts — связка
 * провайдера, trackEvent.ts — аналитика, publicResolve.ts) получают его через
 * resolveAppGuid/resolveAppGuidFromParams как «сохранённое значение вкладки»
 * (п.4 правила), сами пропуск не читая. Отказ обмена страница помечает
 * markPassDegraded — покупка идёт по п.4–5 (сохранённое значение вкладки,
 * иначе web-visitor-id) с меткой passDegraded в чекауте.
 */
import { API_BASE_URL } from '@/config/api-url';
import { getVisitorId } from '@/utils/analytics/visitorId';

export const APP_GUID_KEY = 'qb_app_guid';

/**
 * WEB-1193 (п.5) — синхронно с серверным правилом: `visitorId` в запросе
 * создания оплаты валидируется `@MaxLength(64)`
 * (backend/src/paywalls/dto/paywall.dto.ts:474-481), и class-validator
 * ОТВЕРГАЕТ весь запрос целиком при превышении (не обрезает). До этой
 * карточки `?origin=app&guid=X` подставлялся как есть, без проверки — если
 * приложение клиента прислало guid длиннее, оплата просто не начиналась, а
 * причина оставалась неочевидной ни пользователю, ни саппорту.
 *
 * Решение — усечение НА ФРОНТЕ до того же предела, а не отказ: значение,
 * присланное приложением, всё ещё лучше как частичный идентификатор
 * (оплата проходит), чем полный отказ чекаута из-за длины строки, которую
 * человек не выбирал. Усекаем здесь же (единственная точка входа guid'а из
 * приложения) — оба потребителя, связка провайдера И чекаут, получают ОДНО
 * и то же (уже усечённое) значение, докблок файла об этом требовании выше.
 */
export const APP_GUID_MAX_LENGTH = 64;

/**
 * WEB-1888 (п.5 остатка WEB-1325) — НИЖНЯЯ граница той же грамматики.
 *
 * Зеркало серверной `PUBLIC_GUID_MIN_LENGTH`
 * (backend/src/shared/identity/public-guid.grammar.ts) под сторожем паритета
 * `appGuid.grammarSync.guard.test.ts`. Зачем нужна фронту: приложение клиента
 * может дописать в адрес СВОЙ короткий идентификатор (`?origin=app&guid=abc123`),
 * оплата его примет, а связка покупателя с профилем подписочной платформы
 * ответит 400 — и доступ в приложении не появится. Мы такой guid НЕ подменяем
 * (по нему SDK спрашивает право — money-инвариант выше) и запрос НЕ отменяем
 * (иначе сервер не увидит отказа и сигнал в наблюдение не уйдёт), но
 * интегратор обязан увидеть причину в консоли WebView, а не гадать.
 */
export const APP_GUID_MIN_LENGTH = 8;

/** Уложится ли guid в грамматику публичных дверей (длина). */
export function isAppGuidLengthValid(guid: string): boolean {
  return (
    guid.length >= APP_GUID_MIN_LENGTH && guid.length <= APP_GUID_MAX_LENGTH
  );
}

/**
 * Ядро резолва поверх уже разобранных параметров адреса. Отдельная функция
 * нужна визарду пейвола: он читает параметры роутером (`useSearchParams`), а не
 * из `window.location`.
 */
/**
 * WEB-1416 п.2: best-effort счётчик-пинг backend'у, когда страница оплаты
 * открыта СТАРЫМ форматом ссылки (голый guid, без пропуска `?pass=`).
 * Fire-and-forget по конструкции: НИКОГДА не бросает, НИКОГДА не блокирует
 * резолв guid (та же гарантия, что у `console.warn` рядом) — сеть недоступна/
 * fetch отсутствует (SSR, старое окружение) → тихо no-op. Не несёт сам guid
 * и не ждёт ответа — чистый объёмный сигнал (владелец должен увидеть масштаб
 * легаси-формата перед его отключением, дата отключения — за владельцем).
 */
/**
 * Ревизия WEB-1416 (Ф2): дедуп сигнала — ОДИН пинг на страницу-сессию.
 * resolveAppGuid зовётся десятки раз за визит (запрос пейволла, каждое
 * аналитическое событие, связка провайдера, чекаут) — сигнал на каждый вызов
 * завышал метрику решения об отключении легаси-формата в разы, а лимит ручки
 * (30/мин с IP) выбивался штатным визитом. Модульный флаг дедупит в пределах
 * жизни страницы (работает и в приватном режиме), sessionStorage-маркер —
 * в пределах вкладки (переживает перезагрузку/переходы).
 */
const LEGACY_SIGNAL_SENT_KEY = 'qb_legacy_guid_signal_sent';
let legacySignalSent = false;

/** WEB-1573: чей проект открыл страницу — см. setLegacyPaywallSignalProjectId. */
let legacySignalProjectId: string | null = null;

/**
 * WEB-1584 (регрессия волны 23.08): `resolveAppGuidFromParams` — общая точка
 * входа, её зовут trackEvent/providerProfileLink/чекаут/пейволл — ЛЮБОЙ тест
 * любого из этих файлов, резолвящий `?origin=app&guid=` без `?pass=`, ловил
 * ЛИШНИЙ сетевой вызов от этого сигнала, о котором он знать не обязан (два
 * падения: trackEvent.test.ts — `fetch.mock.calls[0]` больше не аналитика, а
 * сигнал; providerProfileLink.web1139.test.ts —
 * `toHaveBeenCalledTimes(1)` видит 2). Мокать fetch под сигнал в каждом таком
 * тесте — плодить шум по всему дереву тестов ради утилиты, о которой они не
 * должны знать. Вместо этого сигнал по умолчанию молчит под jest.
 *
 * Детект — `process.env.JEST_WORKER_ID` (jest сам выставляет его каждому
 * воркеру, включая однопоточный прогон). Первая попытка была на глобальный
 * `jest` (globalThis.jest) — не сработала: этот проект гоняет ts-jest в
 * CommonJS, где `jest`/`expect`/`describe` инжектятся в scope обёртки
 * тест-модуля, а не в globalThis, так что `typeof globalThis.jest` был
 * всегда 'undefined' даже под тестами (проверено отдельным прогоном).
 * `typeof process !== 'undefined'` — стандартный безопасный guard: в браузере
 * (прод/dev, Vite не полифиллит `process`) сам `typeof` не бросает
 * ReferenceError на необъявленный global, в отличие от прямого обращения
 * `process.env` без guard'а. Дублирующий utils-модуль под import.meta.env
 * (как у config/api-url.ts) здесь избыточен — это единственное место файла,
 * которому нужно отличать тестовую среду.
 */
function isRunningUnderJest(): boolean {
  return (
    typeof process !== 'undefined' &&
    Boolean(process.env && process.env.JEST_WORKER_ID)
  );
}

let legacySignalForcedInTests = false;

/** Только для тестов: включает отправку сигнала под jest (по умолчанию
 * отключена, см. isRunningUnderJest выше). Зовёт только собственный набор
 * тестов сигнала в appGuid.test.ts — остальные тесты сигнал не видят. */
export function __forceEnableLegacyPaywallGuidSignalForTests(): void {
  legacySignalForcedInTests = true;
}

/** Только для тестов: сброс модульного флага дедупа (sessionStorage тесты
 * чистят сами). Продакшен-код это не зовёт. */
export function __resetLegacyPaywallGuidSignalForTests(): void {
  legacySignalSent = false;
  legacySignalProjectId = null;
}

/**
 * WEB-1573 (находка исполнителя, чинится сразу по правилу владельца 25.08).
 *
 * Счётчик легаси-обращений канала «оплата» знал только ЧИСЛО обращений за
 * час и ни одного проекта. Из-за этого требование решения владельца 23.08 —
 * «назвать поимённо, кто ещё ходит по старому пути» — по этому каналу
 * выполнить было нечем: замер 26.08 по боевому счётчику дал ~21 живое
 * обращение в сутки и ни одного имени, то есть предупредить интеграторов
 * адресно было невозможно.
 *
 * Почему модульная переменная, а не аргумент резолва: сигнал уходит из
 * `resolveAppGuidFromParams` — синхронной утилиты, которую зовут десятки
 * мест (аналитика, связка провайдера, чекаут, запрос пейвола), и ни одно из
 * них про проект не знает. Публичные страницы (оплата и квиз) знают его
 * ПЕРВЫМИ — они и называют его здесь ещё на отрисовке, до любого резолва.
 * Не назвали (сигнал ушёл раньше страницы) — пинг просто уходит без имени и
 * считается в общий объём: разбивка деградирует, метрика — нет.
 *
 * Идентификатор проекта СЕКРЕТОМ НЕ ЯВЛЯЕТСЯ: он стоит в публичном адресе
 * страницы оплаты. Сервер всё равно проверяет его формат (`@IsUUID`) —
 * значение становится именем поля в хранилище счётчика.
 */
export function setLegacyPaywallSignalProjectId(
  projectId: string | null | undefined,
): void {
  const trimmed = projectId?.trim();
  legacySignalProjectId = trimmed ? trimmed : null;
}

/**
 * WEB-1416, условие владельца 22.08 к принятой тихой деградации: если ссылка
 * оплаты несла платёжный пропуск `?pass=`, а обмен на verified guid
 * ПРОВАЛИЛСЯ (протух/подделка/отказ сервера/таймаут PASS_EXCHANGE_TIMEOUT_MS
 * в PublicPaywall) — покупка идёт деградированным путём (случайный
 * web-visitor-id, доступ в приложение доедет позже сшивкой по оплате).
 * Деградация владельцем ПРИНЯТА, но обязана оставлять СЛЕД: этот признак
 * чекаут (PaywallScreenWizard → stripeApi.checkout → passDegraded) уносит до
 * metadata Stripe-сессии, чтобы разбор жалобы «заплатил — доступа нет»
 * занимал минуту и частота деградаций была видна.
 *
 * Хранение — как у легаси-сигнала выше: модульный флаг (работает и в
 * приватном режиме) + sessionStorage-маркер (переживает редиректы/перезагрузку
 * в той же вкладке — чекаут может случиться на другой странице визита).
 * Взводит признак ВЫЗЫВАЮЩАЯ сторона (PublicPaywall — таймаут обмена живёт
 * у неё); успешный обмен (resolveAppGuidPass) признак СНИМАЕТ — поздний
 * успех после таймаута означает, что личность всё-таки verified.
 */
export const PASS_DEGRADED_KEY = 'qb_pass_degraded';
let passDegraded = false;

/**
 * WEB-1597: причина, по которой обмен не состоялся, живёт ОТДЕЛЬНЫМ полем
 * рядом с признаком (вариант «А» карточки — сам признак `passDegraded` и его
 * читателей не трогаем). Без причины журнал сервера был вынужден называть
 * класс наугад («пропуск просрочен/отклонён»), хотя знал только факт
 * «обмена нет»: поддержка шла проверять срок жизни пропуска даже при 500-й
 * ошибке или таймауте.
 */
export const PASS_DEGRADED_REASON_KEY = 'qb_pass_degraded_reason';
let passDegradedReason: PassExchangeFailureReason | null = null;

export function markPassDegraded(reason?: PassExchangeFailureReason): void {
  passDegraded = true;
  if (reason) passDegradedReason = reason;
  try {
    sessionStorage.setItem(PASS_DEGRADED_KEY, '1');
    if (reason) sessionStorage.setItem(PASS_DEGRADED_REASON_KEY, reason);
  } catch {
    // приватный режим — модульного флага достаточно в пределах страницы
  }
}

/**
 * Причина деградации, если она известна. Возвращает ТОЛЬКО значение из
 * известного набора: маркер лежит в sessionStorage, куда может заглянуть кто
 * угодно, а отсюда значение уезжает в metadata платёжной сессии и в журнал
 * сервера — мусор туда попасть не должен.
 */
export function getPassDegradedReason(): PassExchangeFailureReason | null {
  if (passDegradedReason) return passDegradedReason;
  try {
    const raw = sessionStorage.getItem(PASS_DEGRADED_REASON_KEY);
    return isPassExchangeFailureReason(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function clearPassDegraded(): void {
  passDegraded = false;
  passDegradedReason = null;
  try {
    sessionStorage.removeItem(PASS_DEGRADED_KEY);
    sessionStorage.removeItem(PASS_DEGRADED_REASON_KEY);
  } catch {
    // приватный режим — маркера и не было
  }
}

export function isPassDegraded(): boolean {
  if (passDegraded) return true;
  try {
    return sessionStorage.getItem(PASS_DEGRADED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Только для тестов: сброс модульного флага признака деградации
 * (sessionStorage тесты чистят сами). Продакшен-код это не зовёт. */
export function __resetPassDegradedForTests(): void {
  passDegraded = false;
  passDegradedReason = null;
}

function reportLegacyPaywallGuidUsage(): void {
  try {
    // WEB-1584: под jest сигнал молчит по умолчанию (см. докблок выше) —
    // соседние тесты, резолвящие app-guid, не должны знать об этом побочном
    // сетевом вызове. Собственный набор тестов сигнала включает его явно.
    if (isRunningUnderJest() && !legacySignalForcedInTests) return;
    if (legacySignalSent) return;
    try {
      if (sessionStorage.getItem(LEGACY_SIGNAL_SENT_KEY)) return;
    } catch {
      // приватный режим — дедупим только модульным флагом
    }
    if (typeof fetch !== 'function') return;
    // WEB-1573: имя проекта едет телом, ТОЛЬКО если страница его назвала.
    // Без имени тело не отправляется вовсе — ручка старше этого поля, и
    // пинг без тела обязан продолжать считаться в общий объём (иначе
    // метрика провалится ровно в окне выката нового фронта).
    const init: RequestInit = legacySignalProjectId
      ? {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: legacySignalProjectId }),
        }
      : { method: 'POST', keepalive: true };
    void fetch(
      `${API_BASE_URL}/public/portal/paywall-guid/legacy-signal`,
      init,
    ).catch(() => {
      // best-effort — сетевой сбой не должен быть заметен вызывающей стороне.
    });
    // Помечаем ПОСЛЕ фактической отправки (недоступный fetch выше — не
    // отправка): сигнал-на-сессию, а не попытка-на-сессию.
    legacySignalSent = true;
    try {
      sessionStorage.setItem(LEGACY_SIGNAL_SENT_KEY, '1');
    } catch {
      // приватный режим — маркер не персистится, флага достаточно
    }
  } catch {
    // fetch может отсутствовать/бросить синхронно в экзотических окружениях
    // (SSR-полифиллы) — резолв guid не должен от этого пострадать.
  }
}

/**
 * Признак «в адресе стоит явный app-guid, дописанный мобильным SDK».
 *
 * Ровно эта комбинация (`origin=app` + непустой `guid`) означает, что ссылку
 * дособрал наш SDK и guid в ней — client-held якорь, по которому SDK потом
 * поллит право (`GET /public/entitlement?guid=`; см. докблок файла, блокер
 * выката 23.08). Экспортируется, чтобы страница оплаты (PublicPaywall) не
 * запускала обмен пропуска поверх этого якоря и не путала «нет обмена» с
 * деградацией личности.
 */
export function hasSdkAppGuid(params: URLSearchParams): boolean {
  return params.get('origin') === 'app' && Boolean(params.get('guid'));
}

export function resolveAppGuidFromParams(params: URLSearchParams): string {
  const rawAppGuid = params.get('guid');
  const appGuid = rawAppGuid
    ? rawAppGuid.slice(0, APP_GUID_MAX_LENGTH)
    : rawAppGuid;
  // Приоритет (блокер выката 23.08, докблок файла): `origin=app` + явный
  // `?guid=` — это дописал мобильный SDK, и ИМЕННО по этому guid он потом
  // спрашивает право. Берём его даже когда рядом лежит `?pass=`, иначе
  // рвётся связка «оплата → поллинг» (право ляжет на серверный guid из
  // пропуска, поллинг SDK его не увидит никогда).
  //
  // Защита WEB-1416 (ревизия Ф1) при этом на месте: сырой `?guid=` БЕЗ
  // `origin=app` личностью не признаётся — он не мог быть дописан SDK, а
  // мог приехать из старой/кривой веб-ссылки и перезаписать обменянный
  // verified guid непроверенным значением из адресной строки.
  const hasPass = Boolean(params.get('pass'));
  if (params.get('origin') === 'app' && appGuid) {
    if (rawAppGuid && rawAppGuid.length > APP_GUID_MAX_LENGTH) {
      // eslint-disable-next-line no-console -- видимость в консоли клиента/
      // WebView для разбора конкретной интеграции; отдельного канала
      // мониторинга у фронта под эту утилиту нет (см. отчёт WEB-1193).
      console.warn(
        `[app-guid] guid из приложения длиннее ${APP_GUID_MAX_LENGTH} символов — усечён, чтобы не сломать оплату молча (сервер отверг бы весь запрос целиком)`, // i18n-allow: сообщение в консоль разработчику интеграции, а не надпись кабинета
      );
    }
    // WEB-1416 п.2: сигнал наблюдаемости — ТОЛЬКО про ЛЕГАСИ-формат ссылки,
    // то есть голый `?guid=` БЕЗ пропуска. Если `?pass=` рядом есть — ссылка
    // уже НОВОГО формата, её просто дособрал SDK своим якорем (блокер выката
    // 23.08): считать это легаси и завышать метрику отключения нельзя.
    // Дедуп на страницу-сессию — см. reportLegacyPaywallGuidUsage.
    if (!hasPass) reportLegacyPaywallGuidUsage();
    try {
      sessionStorage.setItem(APP_GUID_KEY, appGuid);
    } catch {
      // приватный режим — используем значение из URL без персиста
    }
    return appGuid;
  }
  try {
    const stored = sessionStorage.getItem(APP_GUID_KEY);
    if (stored) return stored;
  } catch {
    // приватный режим
  }
  return getVisitorId();
}

/**
 * WEB-1416 п.2: обмен НОВОГО платёжного пропуска (`?pass=`, заменил голый
 * guid в paywallUrl — S2sUsersService.buildPaywallUrl) на верифицированный
 * guid через `POST /public/portal/paywall-guid`
 * (PublicPortalController.resolvePaywallGuidPass — ТОТ ЖЕ HMAC-механизм, что
 * manage_link, TTL=24ч).
 *
 * Асинхронный ПРИМИТИВ. `resolveAppGuidFromParams`/`resolveAppGuid` — синх-
 * ронные и остаются таковыми (многочисленные существующие вызывающие
 * стороны: чекаут, связка провайдера, аналитика — все вне зоны записи этого
 * юнита); зовёт ЭТУ функцию страница пейволла (pages/PublicPaywall.tsx,
 * см. докблок файла «Где подключён обмен пропуска»). При успехе возвращает
 * `{ ok: true, guid }` и персистит guid в sessionStorage ПОД ТЕМ ЖЕ
 * APP_GUID_KEY, что синхронный путь (оба потребителя обязаны получить одно и
 * то же значение, докблок файла выше). Вызывающая сторона НИКОГДА не считает
 * значение личностью покупателя без успешного обмена.
 *
 * WEB-1597 — три РАЗНЫХ исхода, их нельзя путать:
 *  - `null` — обмена НЕ ТРЕБОВАЛОСЬ: в адресе нет `pass`/`origin=app`, либо
 *    ссылку дособрал мобильный SDK и личность уже известна (hasSdkAppGuid,
 *    блокер выката 23.08). Это НЕ деградация, признак взводить нельзя;
 *  - `{ ok: true, guid }` — обмен состоялся;
 *  - `{ ok: false, reason }` — обмен НЕ состоялся, причина названа
 *    (см. PassExchangeFailureReason). Раньше здесь был тот же голый `null`,
 *    что и в первом случае, — и все причины сливались в одну, из-за чего
 *    журнал сервера называл класс наугад («пропуск просрочен/отклонён»).
 */
/**
 * WEB-1597: причины, по которым обмен пропуска мог не состояться. Набор
 * закрытый — он уезжает в metadata платёжной сессии и оттуда в журнал
 * сервера, поэтому «свободного текста» здесь быть не должно.
 *
 * - `rate_limited` — наш собственный ограничитель частоты на ручке обмена
 *   (10 запросов в минуту с адреса, IpThrottlerGuard) ответил 429;
 * - `server_error` — 5xx либо 200 с телом не по контракту (это поломка на
 *   нашей стороне, а НЕ «пропуск отклонён»);
 * - `network` — запрос вообще не доехал (обрыв связи, страница закрывается);
 * - `timeout` — истёк бюджет времени вызывающей стороны (PublicPaywall,
 *   PASS_EXCHANGE_TIMEOUT_MS): обмен, возможно, ещё в пути;
 * - `rejected` — сервер ответил отказом не временного класса (404/403/400):
 *   ЕДИНСТВЕННЫЙ случай, про который честно сказать «пропуск просрочен либо
 *   отклонён».
 */
export type PassExchangeFailureReason =
  | 'rate_limited'
  | 'server_error'
  | 'network'
  | 'timeout'
  | 'rejected';

const PASS_EXCHANGE_FAILURE_REASONS: readonly PassExchangeFailureReason[] = [
  'rate_limited',
  'server_error',
  'network',
  'timeout',
  'rejected',
];

export function isPassExchangeFailureReason(
  value: unknown,
): value is PassExchangeFailureReason {
  return (
    typeof value === 'string' &&
    (PASS_EXCHANGE_FAILURE_REASONS as readonly string[]).includes(value)
  );
}

/** Исход обмена: либо verified guid, либо НАЗВАННАЯ причина отказа. */
export type PassExchangeResult =
  | { ok: true; guid: string }
  | { ok: false; reason: PassExchangeFailureReason };

/**
 * Пауза перед единственным повтором. Умышленно короткая: весь обмен
 * ограничен бюджетом вызывающей стороны (PASS_EXCHANGE_TIMEOUT_MS = 3с в
 * PublicPaywall), за которым страница оплаты грузится без verified guid.
 * Повтор обязан уместиться внутрь этого бюджета вместе с двумя запросами —
 * иначе мы бы лечили неверную подсказку поддержке ценой лишней секунды
 * ожидания на денежной странице.
 */
export const PASS_EXCHANGE_RETRY_DELAY_MS = 400;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function resolveAppGuidPass(
  params: URLSearchParams,
  projectId: string,
): Promise<PassExchangeResult | null> {
  const pass = params.get('pass');
  if (params.get('origin') !== 'app' || !pass) return null;
  // Блокер выката 23.08 (вторая половина фикса): SDK дописал к серверной
  // ссылке СВОЙ `guid` — он и есть личность (по нему SDK поллит право). Обмен
  // пропуска обязан ОТСТУПИТЬ целиком: сходив на бэкенд, он записал бы в
  // sessionStorage (APP_GUID_KEY) СЕРВЕРНЫЙ guid поверх SDK-якоря, который
  // синхронный резолв туда уже положил, — и чекаут ушёл бы не на тот
  // идентификатор. Возврат null здесь означает «обмен не требуется»
  // (личность известна), а не «личность потеряна»: вызывающая сторона
  // (PublicPaywall) отличает эти случаи через hasSdkAppGuid и НЕ помечает
  // покупку деградированной.
  if (hasSdkAppGuid(params)) return null;
  // WEB-1597: ДВЕ попытки максимум, и вторая — только для заведомо временных
  // причин (429 ограничителя частоты и 5xx). Для отказа «пропуск не принят»
  // (404/403/400) повтор бессмысленен: пропуск от повторного запроса не
  // оживёт, а бюджет денежной страницы сгорит.
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await delay(PASS_EXCHANGE_RETRY_DELAY_MS);
    let res: Response;
    try {
      res = await fetch(`${API_BASE_URL}/public/portal/paywall-guid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, pass }),
      });
    } catch {
      // Сеть уже отвалилась — повтор через 400мс её не вернёт, а страница
      // оплаты ждёт. Честно называем причину и отдаём управление.
      return { ok: false, reason: 'network' };
    }
    if (!res.ok) {
      const reason: PassExchangeFailureReason =
        res.status === 429
          ? 'rate_limited'
          : res.status >= 500
            ? 'server_error'
            : 'rejected';
      const retryable = reason === 'rate_limited' || reason === 'server_error';
      if (retryable && attempt === 0) continue;
      return { ok: false, reason };
    }
    let data: { guid?: unknown };
    try {
      data = (await res.json()) as { guid?: unknown };
    } catch {
      // 200 с нечитаемым телом — поломка нашей стороны, не отказ пропуска.
      return { ok: false, reason: 'server_error' };
    }
    if (typeof data.guid !== 'string' || !data.guid) {
      // Тот же класс: контракт ответа нарушен. Повтор не делаем — второй
      // запрос вернёт то же самое тело.
      return { ok: false, reason: 'server_error' };
    }
    const guid = data.guid.slice(0, APP_GUID_MAX_LENGTH);
    try {
      sessionStorage.setItem(APP_GUID_KEY, guid);
    } catch {
      // приватный режим — используем verified-значение без персиста
    }
    // Условие владельца 22.08 (след деградации): успешный обмен = личность
    // verified — снимаем ранее взведённый признак И причину (в т.ч. поздний
    // успех после таймаута PublicPaywall). Взводит признак вызывающая сторона.
    clearPassDegraded();
    return { ok: true, guid };
  }
  // Недостижимо: цикл либо возвращает результат, либо делает ровно один
  // повтор. Ветка нужна компилятору и как страховка от будущей правки.
  return { ok: false, reason: 'server_error' };
}

/** То же, но по текущему адресу страницы (для кода вне роутера). */
export function resolveAppGuid(): string {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    // SSR/битый URL — падаем на сохранённое/visitor-id
    params = new URLSearchParams();
  }
  return resolveAppGuidFromParams(params);
}
